import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { getPoolOverview } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Droplet } from "lucide-react";

const num = (n, d = 4) => (n == null ? "—" : Number(n).toLocaleString(undefined, { maximumFractionDigits: d }));
const KIND_STYLE = { buy: "bg-green-500/10 text-green-400", sell: "bg-red-500/10 text-red-400", swap: "bg-[#D4AF37]/10 text-[#D4AF37]" };

const Stat = ({ title, value, testId }) => (
  <div className="p-3 rounded-lg bg-[#050505] border border-[#2A2411]">
    <p className="text-[11px] text-zinc-500 uppercase tracking-wider">{title}</p>
    <p data-testid={testId} className="font-display text-lg font-bold text-white break-all">{value}</p>
  </div>
);

const TradeRow = ({ t }) => (
  <tr data-testid="pool-trade-row" className="border-t border-[#2A2411]">
    <td className="py-2 pr-3"><span className={`text-[10px] uppercase font-bold px-1.5 py-0.5 rounded ${KIND_STYLE[t.kind]}`}>{t.kind}</span></td>
    <td className="py-2 pr-3 font-mono text-xs text-zinc-300 whitespace-nowrap">
      {t.direction === "FMI→USDT" ? `${num(t.fmi)} FMI → ${num(t.usdt, 2)} USDT${t.kind === "sell" ? " (gross)" : ""}` : `${num(t.usdt, 2)} USDT${t.kind === "buy" ? " (net)" : ""} → ${num(t.fmi)} FMI`}
    </td>
    <td className="py-2 pr-3 font-mono text-xs text-white">{num(t.price)}</td>
    <td className="py-2 pr-3"><Link to={`/address/${t.trader}`} className="table-link font-mono text-xs">{t.trader.slice(0, 10)}…</Link></td>
    <td className="py-2 pr-3 text-xs text-zinc-500 whitespace-nowrap">{t.timestamp ? new Date(t.timestamp * 1000).toLocaleString() : "—"}</td>
    <td className="py-2"><Link to={`/tx/${t.txHash}`} className="table-link font-mono text-xs">{t.txHash.slice(0, 10)}…</Link></td>
  </tr>
);

export const PoolOverview = () => {
  const [page, setPage] = useState(1);
  const { data, isError } = useQuery({
    queryKey: ["pool", page], queryFn: () => getPoolOverview(page), refetchInterval: 8000, placeholderData: keepPreviousData,
  });

  return (
    <Card data-testid="pool-overview" className="border-[#2A2411] bg-[#0A0A0A] mt-6">
      <CardHeader className="pb-3">
        <CardTitle className="text-base font-display flex items-center gap-2"><Droplet size={16} className="text-[#D4AF37]" /> FMI / USDT Pool</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {isError && <p data-testid="pool-error" className="text-sm text-zinc-500">Pool data unavailable</p>}
        {data && (
          <>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              <Stat title="FMI reserve" value={`${num(data.reserves.FMI, 2)} FMI`} testId="pool-reserve-fmi" />
              <Stat title="USDT reserve" value={`${num(data.reserves.USDT, 2)} USDT`} testId="pool-reserve-usdt" />
              <Stat title="Price" value={`${num(data.price)} USDT`} testId="pool-price" />
              <Stat title="Pool fee" value={`${data.feePercent}%`} testId="pool-fee" />
              <Stat title="Total trades" value={data.totalTrades.toLocaleString()} testId="pool-total-trades" />
              <Stat title="Volume" value={`${num(data.volumeUsdt, 2)} USDT`} testId="pool-volume" />
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead>
                  <tr className="text-[11px] text-zinc-500 uppercase tracking-wider">
                    <th className="pb-2 pr-3 font-medium">Type</th><th className="pb-2 pr-3 font-medium">Amounts</th>
                    <th className="pb-2 pr-3 font-medium">Price</th><th className="pb-2 pr-3 font-medium">Trader</th>
                    <th className="pb-2 pr-3 font-medium">Time</th><th className="pb-2 font-medium">Tx</th>
                  </tr>
                </thead>
                <tbody>{data.trades.map((t) => <TradeRow key={`${t.txHash}-${t.logIndex}`} t={t} />)}</tbody>
              </table>
              {data.trades.length === 0 && <p data-testid="pool-trades-empty" className="text-sm text-zinc-500 py-3">No trades yet</p>}
            </div>
            <div className="flex items-center justify-between">
              <Button data-testid="pool-prev-page" variant="outline" size="sm" disabled={page === 1} onClick={() => setPage((p) => p - 1)}
                className="border-[#2A2411] bg-transparent text-zinc-300">Newer</Button>
              <span data-testid="pool-page" className="text-xs text-zinc-500">Page {data.page}</span>
              <Button data-testid="pool-next-page" variant="outline" size="sm" disabled={!data.hasMore} onClick={() => setPage((p) => p + 1)}
                className="border-[#2A2411] bg-transparent text-zinc-300">Older</Button>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};
